import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  Column,
  Columns,
  Dropdown,
  IconDelete,
  IconResume,
} from 'braid-design-system';

import type { Resume } from 'src/__generated__/graphql';
import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import { useDialogRouterState } from 'src/modules/use-dialog-router-state';
import { formatDateShort } from 'src/modules/utils';
import { DocumentDeleteDialog } from 'src/pages/Documents/components/DocumentDeleteDialog/DocumentDeleteDialog';
import { DocumentDownloadButton } from 'src/pages/Documents/components/DocumentDownloadButton/DocumentDownloadButton';
import { DocumentSensitive } from 'src/pages/Documents/components/DocumentSensitive/DocumentSensitive';

import translations from './.vocab';

interface DocumentSelectProps {
  id: string;
  loading?: boolean;
  defaultValue?: File | string | null;
  value: string;
  documentList?: Resume[];
  onChange: (value: string) => void;
  valid: boolean;
  message: string;
}

const DONT_INCLUDE_RESUME_ID = 'dont-include';

const DocumentSelect = ({
  id,
  loading = false,
  defaultValue,
  value,
  documentList = [],
  onChange,
  valid,
  message,
}: DocumentSelectProps) => {
  const { t } = useTranslations(translations);
  const client = useTrackingClient();
  const {
    isOpen: deleteDocumentDialogOpen,
    open: openDeleteDocumentDialog,
    close: closeDeleteDocumentDialog,
  } = useDialogRouterState(`delete-document-${id}`);

  const hasResumeSelected = value && value !== DONT_INCLUDE_RESUME_ID;
  const hasSpi = documentList.find((doc) => doc.id === value)?.fileMetadata
    .sensitiveDataInfo?.isDetected;

  const getTone = () => {
    if (!valid || hasSpi) {
      return 'critical';
    }
    return 'positive';
  };

  const getMessage = () => {
    if (valid && hasResumeSelected) {
      return (
        <Box component="span" role="status">
          {hasSpi ? <DocumentSensitive /> : t('Resume attached')}
        </Box>
      );
    }
    return message;
  };

  const selections = documentList.map((doc) => (
    <option key={doc.id} value={doc.id}>
      {`${formatDateShort(doc.createdDateUtc)} - ${doc.fileMetadata?.name}`}
    </option>
  ));

  const handleChange = (e: React.FormEvent<HTMLSelectElement>) => {
    const currentResumeId = e.currentTarget.value;
    const selection =
      currentResumeId === DONT_INCLUDE_RESUME_ID
        ? currentResumeId
        : 'not-preselected';
    const resumeSelectedType =
      currentResumeId === defaultValue ? 'preselected' : selection;

    client.trackLink({
      link: 'resume_dropdown_pressed',
      eventName: 'resume_dropdown_pressed',
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.RESUME,
      resumeSelectedType,
    });
    onChange(e.currentTarget.value);
  };

  const clearSelection = () => onChange('');

  return (
    <>
      <Columns space="small" alignY="center">
        <Column>
          <Dropdown
            data={{ testid: 'select-input' }}
            id={id}
            label=""
            placeholder={t('Please select a resume')}
            value={value || ''}
            disabled={loading}
            icon={<IconResume />}
            onChange={handleChange}
            message={getMessage()}
            tone={getTone()}
          >
            {selections}
          </Dropdown>
        </Column>
        {hasResumeSelected ? (
          <Column width="content">
            <Box paddingBottom="medium">
              <Columns space="small">
                <Column>
                  <ButtonIcon
                    id="deleteResume"
                    label={t('Delete')}
                    icon={<IconDelete />}
                    onClick={openDeleteDocumentDialog}
                    data={{ testid: 'deleteButton' }}
                  />
                </Column>
                <Column width="content">
                  <DocumentDownloadButton
                    id="downloadResume"
                    resumeId={value}
                  />
                </Column>
              </Columns>
            </Box>
          </Column>
        ) : null}
        <DocumentDeleteDialog
          resumeId={value}
          open={Boolean(value && deleteDocumentDialogOpen)}
          onClose={closeDeleteDocumentDialog}
          onSuccess={() => {
            clearSelection();
            closeDeleteDocumentDialog();
          }}
        />
      </Columns>
    </>
  );
};

export { DocumentSelect, DONT_INCLUDE_RESUME_ID };
